import React from 'react';

import { useQuery } from '@apollo/client';
import { Box, Card, Container, Typography } from '@mui/material';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { GET_LIST_PARTNERS_BY_FILTER } from '../../graphql/partners';
import { BREAKPOINTS_VALUES, useIsWidthUp, useWidth } from '../../theme';
import IKImage from '../IKImage/IKImage';
import { Loader } from '../Loader';
import useStyles from './styles';

const defaultCount = {
  md: 4,
  lg: 5,
  xl: 6,
  ml: 6,
};

const calcIsSlidable = (widthBreakpoint, contentLength) => {
  return defaultCount[widthBreakpoint] && contentLength > defaultCount[widthBreakpoint];
};

const getSwiperSettings = (isSlidable): SwiperOptions => ({
  slidesPerView: 2.5,
  spaceBetween: 16,
  observer: true,
  noSwiping: !isSlidable,
  ...(isSlidable && {
    pagination: {
      clickable: true,
    },
    navigation: true,
  }),
  breakpoints: {
    [BREAKPOINTS_VALUES.xl]: {
      slidesPerView: 6,
    },
    [BREAKPOINTS_VALUES.lg]: {
      slidesPerView: 5,
    },
    [BREAKPOINTS_VALUES.md]: {
      slidesPerView: 4,
      ...(isSlidable && {
        mousewheel: {
          releaseOnEdges: true,
          forceToAxis: true,
          invert: true,
        },
      }),
    },
  },
});

const BlockByType = ({
  partners,
  displayType,
  classes,
  heading,
  description,
  whiteBg,
  isMobile,
}) => {
  const { t } = useTranslation();

  const width = useWidth();

  const isSlidable = calcIsSlidable(width, partners?.length || 0);
  const swiperSettings = getSwiperSettings(isSlidable);

  switch (displayType) {
    case 'Block':
      return (
        <Box className={cx(classes.cardsWrapper, 'partners-type-block')}>
          <Typography variant="h2" component="h2" align="center" className={classes.headingBlock}>
            {heading}
          </Typography>

          {partners?.map((item) => {
            return (
              <Card key={item.id} className={classes.card}>
                <IKImage
                  path={item.image_url}
                  alt={item.title}
                  draggable={false}
                  srcset={[{ width: 170 }, { width: 270 }, { width: 140 }, { width: 180 }]}
                  sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 170px, (max-width:${BREAKPOINTS_VALUES['md']}px) 270px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 140px, 180px`}
                />
              </Card>
            );
          })}
        </Box>
      );
    case 'Slider':
      return isMobile ? (
        <Box className={cx(classes.cardsWrapper, 'partners-type-block')}>
          <Typography variant="h2" component="h2" align="center" className={classes.headingBlock}>
            {heading}
          </Typography>

          {partners?.map((item) => {
            return (
              <Card key={item.id} className={classes.card}>
                <IKImage
                  path={item.image_url}
                  alt={item.title}
                  draggable={false}
                  srcset={[{ width: 170 }, { width: 270 }, { width: 140 }, { width: 180 }]}
                  sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 170px, (max-width:${BREAKPOINTS_VALUES['md']}px) 270px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 140px, 180px`}
                />
              </Card>
            );
          })}
        </Box>
      ) : (
        <div
          className={cx(
            isSlidable ? classes.swiperWrap : classes.swiperWrapCentered,
            'partners-type-slider',
          )}
        >
          {isSlidable !== undefined && (
            <Swiper {...swiperSettings}>
              {partners?.map((item) => {
                return (
                  <SwiperSlide key={item.id}>
                    <Card
                      className={cx(classes.card, classes.cardSlider, whiteBg && classes.whiteBg)}
                    >
                      <IKImage
                        path={item.image_url}
                        alt={item.title}
                        draggable={false}
                        srcset={[{ width: 200 }, { width: 270 }, { width: 320 }, { width: 220 }]}
                        sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 200px, (max-width:${BREAKPOINTS_VALUES['md']}px) 270px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 320px, 220px`}
                      />
                    </Card>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          )}
        </div>
      );
    case 'WithDescription':
      return (
        <Box className={cx(classes.partnersDescWrapper, 'partners-type-description')}>
          {heading && (
            <Typography variant="h2" component="h2" align="center" className={classes.headingDesc}>
              {heading}
            </Typography>
          )}

          {description && (
            <Typography className={classes.partnerDescBlockDesc}>{description}</Typography>
          )}

          <Box className={classes.cardsDescWrapper}>
            {partners?.map((item) => {
              return (
                <Card key={item?.id} className={classes.cardDesc}>
                  <div className={cx(classes.descImgWrap, whiteBg && classes.whiteBg)}>
                    <IKImage
                      path={item.image_url}
                      alt={item.title}
                      draggable={false}
                      srcset={[{ width: 500 }, { width: 600 }, { width: 850 }, { width: 900 }]}
                      sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 500px, (max-width:${BREAKPOINTS_VALUES['md']}px) 600px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 850px, 900px`}
                    />
                  </div>

                  <Typography className={classes.partnerDesc}>{item?.description}</Typography>
                </Card>
              );
            })}
          </Box>
        </Box>
      );
    default:
      return <div>{t('block.partners.empty')}</div>;
  }
};

export const PartnersBlock = ({ data: partnersData, preventLoader }) => {
  const background = partnersData?.background;
  const displayType = partnersData?.display;
  const whiteBg = partnersData?.white_icon;
  const isMobile = !useIsWidthUp('md');

  const classes = useStyles(background, displayType)();
  const partnerTypeId = partnersData?.partnerType?.id;
  const partnersList = partnersData?.partnersList;

  const {
    data,
    loading,
    // error
  } = useQuery(GET_LIST_PARTNERS_BY_FILTER, {
    variables: {
      filter: {
        type_id: partnerTypeId,
      },
    },
    skip: !partnerTypeId || partnersList,
  });

  if (loading) {
    return preventLoader ? null : <Loader />;
  }

  const heading =
    typeof partnersData?.heading === 'string' ? partnersData?.heading : partnersData?.heading?.en;
  const description = partnersData?.description;
  const partners = partnersList || data?.listPartners;

  return (
    <Box
      className={cx(
        classes.wrapper,
        background === 'Dark' ? 'dark-block' : 'light-block',
        'partners-block',
      )}
    >
      <Container className={classes.container}>
        {heading && (
          <Typography variant="h2" component="h2" align="center" className={classes.heading}>
            {heading}
          </Typography>
        )}
        <BlockByType
          partners={partners}
          displayType={displayType}
          classes={classes}
          heading={heading}
          description={description}
          whiteBg={whiteBg}
          isMobile={isMobile}
        />
      </Container>
    </Box>
  );
};

export default PartnersBlock;
